import React, { useState, useRef, lazy, Component } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { CalendarIcon, ListIcon, GridIcon, ChevronLeftIcon, ChevronRightIcon, ClockIcon, MapPinIcon, BookIcon, ShoppingBagIcon, BellIcon, StarIcon, XIcon, SparklesIcon, BookmarkIcon } from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useRoleTheme } from '../hooks/useRoleTheme';
import SaveToTracker from '../components/UI/SaveToTracker';
import SaveEventButton from '../components/Events/SaveEventButton';
import { getBookSpineColor } from '../utils/genreColors';
// Types
type ViewMode = 'calendar' | 'list' | 'grid';
type TabType = 'all' | 'releases' | 'events' | 'saved';
interface TrackerItem {
  id: string | number;
  type: 'book' | 'merch' | 'event';
  title: string;
  subtitle?: string;
  date: string;
  endDate?: string;
  image: string;
  genre?: string;
  eventType?: string;
  price?: number;
  status?: 'preorder' | 'coming_soon' | 'new' | 'upcoming';
  location?: string;
  time?: string;
  series?: {
    name: string;
    position: number;
  };
  isSaved?: boolean;
}
// Helper to get current dates
const now = new Date();
const currentYear = now.getFullYear();
const currentMonth = now.getMonth();
const formatDate = (day: number): string => {
  return `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
};
// Helper to get detail URL for an item
const getItemDetailUrl = (item: TrackerItem): string => {
  if (item.type === 'book') {
    return `/book/${item.id}`;
  } else if (item.type === 'merch') {
    return `/browse?type=merch`;
  } else if (item.type === 'event') {
    if (item.eventType === 'Book Signing') return '/events/book-signings';
    if (item.eventType === 'Virtual Event') return '/events/book-clubs/virtual';
    if (item.eventType === 'Book Club') return '/events/book-clubs/in-person';
    return '/events';
  }
  return '/';
};
// Helper function to get color for genre (matching UnifiedBookCard)
const getGenreColor = (genre: string): string => {
  const genreLower = genre.toLowerCase();
  if (genreLower.includes('romance')) return 'bg-pink-600';
  if (genreLower.includes('fantasy')) return 'bg-indigo-600';
  if (genreLower.includes('sci-fi') || genreLower.includes('science')) return 'bg-blue-600';
  if (genreLower.includes('mystery')) return 'bg-amber-600';
  if (genreLower.includes('thriller')) return 'bg-red-600';
  if (genreLower.includes('horror')) return 'bg-gray-800';
  if (genreLower.includes('historical')) return 'bg-amber-700';
  return 'bg-violet-600';
};
// Helper function to get status color
const getStatusColor = (status?: string): string => {
  if (!status) return 'bg-gray-600';
  if (status === 'preorder') return 'bg-yellow-500';
  if (status === 'coming_soon') return 'bg-blue-600';
  if (status === 'new') return 'bg-green-600';
  return 'bg-indigo-600';
};
// Mock Data
const mockItems: TrackerItem[] = [{
  id: 'r1',
  type: 'book',
  title: 'The Broken Echo',
  subtitle: 'Elena Richards',
  date: formatDate(15),
  image: 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  genre: 'Romance',
  price: 24.99,
  status: 'preorder',
  series: {
    name: 'Echo Series',
    position: 2
  },
  isSaved: true
}, {
  id: 'r2',
  type: 'book',
  title: 'Midnight Whispers',
  subtitle: 'Sarah Chen',
  date: formatDate(20),
  image: 'https://images.unsplash.com/photo-1543002588-bfa74002ed7e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  genre: 'Fantasy',
  price: 19.99,
  status: 'coming_soon',
  isSaved: true
}, {
  id: 'r3',
  type: 'merch',
  title: 'Starfall T-Shirt',
  subtitle: 'Cosmic Creations',
  date: formatDate(10),
  image: 'https://images.unsplash.com/photo-1576566588028-4147f3842f27?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: 24.99,
  status: 'new',
  isSaved: false
}, {
  id: 'r4',
  type: 'book',
  title: "Dragon's Crown",
  subtitle: 'Marcus Chen',
  date: formatDate(22),
  image: 'https://images.unsplash.com/photo-1626788353765-b55f9fc8daf9?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  genre: 'Fantasy',
  price: 29.99,
  status: 'preorder',
  isSaved: true
}, {
  id: 'r5',
  type: 'book',
  title: 'The Last Detective',
  subtitle: 'James Miller',
  date: formatDate(18),
  image: 'https://images.unsplash.com/photo-1633477189729-9290b3261d0a?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  genre: 'Mystery',
  price: 14.99,
  status: 'coming_soon',
  isSaved: false
}, {
  id: 'e1',
  type: 'event',
  title: 'Book Signing: The Silent Echo',
  subtitle: 'Elena Richards',
  date: formatDate(12),
  time: '2:00 PM',
  location: 'Bright Pages Bookstore',
  eventType: 'Book Signing',
  image: 'https://images.unsplash.com/photo-1528605105345-5344ea20e269?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  isSaved: true
}, {
  id: 'e2',
  type: 'event',
  title: 'Virtual Q&A',
  subtitle: 'Elena Richards',
  date: formatDate(15),
  time: '7:00 PM',
  location: 'Zoom',
  eventType: 'Virtual Event',
  image: 'https://images.unsplash.com/photo-1517971129774-8a2b38fa128e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  isSaved: false
}, {
  id: 'e3',
  type: 'event',
  title: 'Romance Readers Book Club',
  subtitle: 'Monthly Meetup',
  date: formatDate(20),
  time: '6:30 PM',
  location: 'City Library',
  eventType: 'Book Club',
  image: 'https://images.unsplash.com/photo-1532012197267-da84d127e765?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  isSaved: true
}, {
  id: 'e4',
  type: 'event',
  title: 'Fantasy Festival',
  subtitle: 'Various Authors',
  date: formatDate(25),
  endDate: formatDate(26),
  time: 'All Day',
  location: 'Convention Center',
  eventType: 'Festival',
  image: 'https://images.unsplash.com/photo-1513475382585-d06e58bcb0e0?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  isSaved: false
}];
// Discovery Items for New Releases section
const discoveryItems: TrackerItem[] = [{
  id: 'd1',
  type: 'book',
  title: 'Shadows of Light',
  subtitle: 'A.R. Thorne',
  date: formatDate(28),
  image: 'https://images.unsplash.com/photo-1512820790803-83ca734da794?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  genre: 'Fantasy',
  price: 22.99,
  status: 'upcoming'
}, {
  id: 'd2',
  type: 'book',
  title: 'The Silent Sea',
  subtitle: 'Maria Garcia',
  date: formatDate(30),
  image: 'https://images.unsplash.com/photo-1495446815901-a7297e633e8d?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  genre: 'Thriller',
  price: 18.99,
  status: 'new'
}, {
  id: 'd3',
  type: 'merch',
  title: 'Bookish Candle Set',
  subtitle: 'Wick & Wax',
  date: formatDate(29),
  image: 'https://images.unsplash.com/photo-1603006905003-be475563bc59?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  price: 34.99,
  status: 'new'
}, {
  id: 'd4',
  type: 'book',
  title: 'Neon City Blues',
  subtitle: 'K.J. Sterling',
  date: formatDate(31),
  image: 'https://images.unsplash.com/photo-1516414447565-b14be0adf13e?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80',
  genre: 'Sci-Fi',
  price: 21.99,
  status: 'preorder'
}];
const MyTracker = () => {
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [viewMode, setViewMode] = useState<ViewMode>('calendar');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [hoveredItem, setHoveredItem] = useState<TrackerItem | null>(null);
  const [hoverPosition, setHoverPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [mobileSelectedItem, setMobileSelectedItem] = useState<TrackerItem | null>(null);
  const hoverCardRef = useRef<HTMLDivElement>(null);
  // Filter items based on active tab
  const filteredItems = mockItems.filter(item => {
    if (activeTab === 'all') return true;
    if (activeTab === 'releases') return item.type === 'book' || item.type === 'merch';
    if (activeTab === 'events') return item.type === 'event';
    if (activeTab === 'saved') return item.isSaved === true;
    return true;
  });
  // Calendar Helpers
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const days = new Date(year, month + 1, 0).getDate();
    const firstDay = new Date(year, month, 1).getDay();
    return {
      days,
      firstDay
    };
  };
  const getItemsByDate = (day: number) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return filteredItems.filter(item => item.date === dateStr);
  };
  const getItemColor = (item: TrackerItem) => {
    if (item.type === 'book' && item.genre) {
      return getBookSpineColor(item.genre);
    }
    if (item.type === 'merch') return '#6B7280';
    if (item.type === 'event') return '#4F46E5';
    return '#525252';
  };
  // Handle mouse move for hover card positioning
  const handleMouseMove = (e: React.MouseEvent, item: TrackerItem) => {
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const cardHeight = 200;
    const cardWidth = 288;
    let x = e.clientX + 15;
    let y = e.clientY + 15;
    if (x + cardWidth > viewportWidth - 20) {
      x = e.clientX - cardWidth - 15;
    }
    if (y + cardHeight > viewportHeight - 20) {
      y = e.clientY - cardHeight - 15;
    }
    if (y < 20) {
      y = 20;
    }
    setHoverPosition({
      x,
      y
    });
    setHoveredItem(item);
  };
  // Hover Card Component
  const HoverCard = ({
    item
  }: {
    item: TrackerItem;
  }) => {
    if (!hoverPosition) return null;
    return <div ref={hoverCardRef} className={`fixed z-[9999] w-72 p-3 rounded-xl shadow-xl border pointer-events-none ${dk.cardElevated}`} style={{
      left: hoverPosition.x,
      top: hoverPosition.y
    }} data-id="element-10782" data-mp="ne9k9ifj13hd">
        <div className="flex gap-3" data-id="element-10783" data-mp="11bofj3sgo5h9">
          <img src={item.image} alt={item.title} className="w-16 h-24 object-cover rounded-md shadow-sm flex-shrink-0" data-id="element-10784" data-mp="11cr2j0mnxnrw" />
          <div className="flex-1 min-w-0" data-id="element-10785" data-mp="sa82spe6br9v">
            <h4 className="font-bold text-sm leading-tight mb-1 line-clamp-2" data-id="element-10786" data-mp="sdcdm5m59l9i">
              {item.title}
            </h4>
            <p className={`text-xs mb-2 ${dk.textMuted}`} data-id="element-10787" data-mp="3d1qooitejzb3">{item.subtitle}</p>
            <div className="space-y-1" data-id="element-10788" data-mp="3d3l81sp0zjd2">
              <div className="flex items-center text-xs" data-id="element-10789" data-mp="3h3c9cqc6hhvu">
                <ClockIcon className="h-3 w-3 mr-1.5 opacity-70" data-id="element-10790" data-mp="3h0r3zrco8jtc" />
                <span data-id="element-10791" data-mp="39i9u3k0vv2zu">
                  {new Date(item.date).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric'
                })}
                  {item.time && ` • ${item.time}`}
                </span>
              </div>
              {item.location && <div className="flex items-center text-xs" data-id="element-10792" data-mp="3ejfix6rfa643">
                  <MapPinIcon className="h-3 w-3 mr-1.5 opacity-70" data-id="element-10793" data-mp="2qsa32rlhfdts" />
                  <span className="truncate" data-id="element-10794" data-mp="2lu07ifvuib7n">{item.location}</span>
                </div>}
              {item.price && <div className="font-bold text-sm mt-1" data-id="element-10795" data-mp="30jy1jcgghv4r">${item.price}</div>}
            </div>
          </div>
        </div>
        <div className="mt-3 flex items-center justify-between" data-id="element-10796" data-mp="2vq3a1mum0bv4">
          <div className="flex gap-1" data-id="element-10797" data-mp="281k59harqh7s">
            {item.type === 'book' && item.genre && <span className="px-2 py-0.5 text-[10px] uppercase font-bold rounded-full text-white" style={{
            backgroundColor: getBookSpineColor(item.genre)
          }} data-id="element-10798" data-mp="232fwgrrralhz">
                {item.genre}
              </span>}
            {item.type === 'event' && <span className={`px-2 py-0.5 text-[10px] uppercase font-bold rounded-full ${dk.isDark ? 'bg-indigo-900 text-indigo-200' : 'bg-indigo-100 text-indigo-800'}`} data-id="element-10799" data-mp="31maqrfgu7y06">
                {item.eventType}
              </span>}
            {item.type === 'merch' && <span className={`px-2 py-0.5 text-[10px] uppercase font-bold rounded-full ${dk.badgeNeutral}`} data-id="element-10800" data-mp="2itgmbfguboa7">
                Merch
              </span>}
          </div>
          <span className={`text-xs font-medium ${rt.accentLink}`} data-id="element-10801" data-mp="3u958w5q6mlah">
            Click to view →
          </span>
        </div>
      </div>;
  };
  // Mobile Popup Component
  const MobileItemPopup = ({
    item,
    onClose
  }: {
    item: TrackerItem;
    onClose: () => void;
  }) => {
    return <div className="fixed inset-0 z-[9999] md:hidden" onClick={onClose} data-id="element-10802" data-mp="1ikqoeznu4su7">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" data-id="element-10803" data-mp="1du1t1991bbps" />

        {/* Popup Card */}
        <div className={`absolute bottom-0 left-0 right-0 rounded-t-2xl shadow-2xl animate-slide-up pb-safe ${dk.cardElevated}`} onClick={e => e.stopPropagation()} data-id="element-10804" data-mp="18w0aqqjfngfd">
          {/* Handle bar */}
          <div className="flex justify-center pt-3 pb-2" data-id="element-10805" data-mp="ztzjqbeslqm5">
            <div className={`w-10 h-1 rounded-full ${dk.isDark ? 'bg-gray-600' : 'bg-gray-300'}`} data-id="element-10806" data-mp="uvfrjt9lvilc" />
          </div>

          {/* Close button */}
          <button onClick={onClose} className={`absolute top-3 right-3 p-2 rounded-full ${dk.badgeNeutral}`} data-id="element-10807" data-mp="pyz6j7xpdkch">
            <XIcon className="h-5 w-5" data-id="element-10808" data-mp="2kadxtt6d3ruc" />
          </button>

          <div className="px-5 pb-6" data-id="element-10809" data-mp="2k8szdsgxfor7">
            {/* Content */}
            <div className="flex gap-4" data-id="element-10810" data-mp="2sbuzk9ng0gxi">
              <img src={item.image} alt={item.title} className="w-24 h-36 object-cover rounded-lg shadow-md flex-shrink-0" data-id="element-10811" data-mp="2xaeqkogw8iwq" />
              <div className="flex-1 min-w-0 py-1" data-id="element-10812" data-mp="2ifi5hanzk128">
                <h4 className={`font-bold text-lg leading-tight mb-1 line-clamp-2 ${dk.textHeading}`} data-id="element-10813" data-mp="2naxk44tti0zp">
                  {item.title}
                </h4>
                <p className={`text-sm mb-3 ${dk.textMuted}`} data-id="element-10814" data-mp="2eclvu9gfbfaf">
                  {item.subtitle}
                </p>

                <div className="space-y-2" data-id="element-10815" data-mp="1zo7kfa4xyii9">
                  <div className={`flex items-center text-sm ${dk.textSecondary}`} data-id="element-10816" data-mp="24lqz2b5jwxaq">
                    <CalendarIcon className="h-4 w-4 mr-2 opacity-70" data-id="element-10817" data-mp="1qlpvup1tdds3" />
                    <span data-id="element-10818" data-mp="1unjt0ncsvzni">
                      {new Date(item.date).toLocaleDateString(undefined, {
                      weekday: 'short',
                      month: 'long',
                      day: 'numeric'
                    })}
                    </span>
                  </div>
                  {item.time && <div className={`flex items-center text-sm ${dk.textSecondary}`} data-id="element-10819" data-mp="1l10gvphscmte">
                      <ClockIcon className="h-4 w-4 mr-2 opacity-70" data-id="element-10820" data-mp="1fzjgrw74l4dv" />
                      <span data-id="element-10821" data-mp="1ux3jewgjungv">{item.time}</span>
                    </div>}
                  {item.location && <div className={`flex items-center text-sm ${dk.textSecondary}`} data-id="element-10822" data-mp="1pys96kmceay4">
                      <MapPinIcon className="h-4 w-4 mr-2 opacity-70" data-id="element-10823" data-mp="1zotuj4nb9f9c" />
                      <span className="truncate" data-id="element-10824" data-mp="1zpn40ddnmiv6">{item.location}</span>
                    </div>}
                </div>
              </div>
            </div>

            {/* Tags and Price */}
            <div className="flex items-center justify-between mt-4 mb-5" data-id="element-10825" data-mp="28nfen48fl2c8">
              <div className="flex gap-2 flex-wrap" data-id="element-10826" data-mp="16dovjg5xdiei">
                {item.type === 'book' && item.genre && <span className="px-3 py-1 text-xs uppercase font-bold rounded-full text-white" style={{
                backgroundColor: getBookSpineColor(item.genre)
              }} data-id="element-10827" data-mp="1adz1wtxmqatw">
                    {item.genre}
                  </span>}
                {item.type === 'event' && <span className={`px-3 py-1 text-xs uppercase font-bold rounded-full ${dk.isDark ? 'bg-indigo-900 text-indigo-200' : 'bg-indigo-100 text-indigo-800'}`} data-id="element-10828" data-mp="1u0524eegmdm1">
                    {item.eventType}
                  </span>}
                {item.type === 'merch' && <span className={`px-3 py-1 text-xs uppercase font-bold rounded-full ${dk.badgeNeutral}`} data-id="element-10829" data-mp="3rn022puj0h3">
                    Merch
                  </span>}
                {item.status && <span className={`px-3 py-1 text-xs font-medium rounded-full ${dk.isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-50 text-green-600'}`} data-id="element-10830" data-mp="3siguqauacjet">
                    {item.status.replace('_', ' ')}
                  </span>}
              </div>
              {item.price && <span className={`text-xl font-bold ${dk.textHeading}`} data-id="element-10831" data-mp="k1cce1q2ka9a">
                  ${item.price}
                </span>}
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3" data-id="element-10832" data-mp="a7ddrcp7f8m3">
              <Link to={getItemDetailUrl(item)} className={`flex-1 py-3 px-4 text-center font-semibold rounded-xl active:scale-[0.98] transition-all ${rt.primaryButton}`} onClick={onClose} data-id="element-10833" data-mp="64p2yzgxjrmh">
                View Details
              </Link>
              <div onClick={e => e.preventDefault()} data-id="element-10834" data-mp="tsh1uizyozqa">
                {item.type === 'event' ? <SaveEventButton eventId={Number(item.id)} isSaved={true} onToggleSave={() => {}} data-id="element-10835" data-mp="yto8ufua5uvw" /> : <SaveToTracker productId={String(item.id)} data-id="element-10836" data-mp="psygjm48ammx" />}
              </div>
            </div>
          </div>
        </div>
      </div>;
  };
  const renderCalendar = () => {
    const {
      days,
      firstDay
    } = getDaysInMonth(currentDate);
    const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return <div className={`rounded-xl shadow-sm overflow-hidden ${dk.card}`} data-id="element-10837" data-mp="1wfc6f42h2j90">
        <div className={`p-4 flex items-center justify-between border-b ${dk.border}`} data-id="element-10838" data-mp="216tz74ucddiw">
          <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1))} className={`p-2 rounded-full transition-colors ${dk.textMuted} hover:${dk.textPrimary} hover:${dk.surfaceMuted}`} data-id="element-10839" data-mp="2aldrcwm8vacg">
            <ChevronLeftIcon className="h-5 w-5" data-id="element-10840" data-mp="1rmi3e30zbs72" />
          </button>
          <h2 className={`text-lg font-bold ${dk.textHeading}`} data-id="element-10841" data-mp="1roupmsdrsopx">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h2>
          <button onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1))} className={`p-2 rounded-full transition-colors ${dk.textMuted} hover:${dk.textPrimary} hover:${dk.surfaceMuted}`} data-id="element-10842" data-mp="21m0ia5218ns9">
            <ChevronRightIcon className="h-5 w-5" data-id="element-10843" data-mp="c1ev5puvnxqp" />
          </button>
        </div>

        <div className={`grid grid-cols-7 gap-px ${dk.isDark ? 'bg-gray-700' : 'bg-gray-200'}`} data-id="element-10844" data-mp="72k9mg5izv2w">
          {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => <div key={day} className={`p-2 text-center text-xs font-semibold uppercase tracking-wider ${dk.surfaceMuted} ${dk.textMuted}`} data-id="element-10845" data-mp="gynbrm0dntpu">
              {day}
            </div>)}

          {Array.from({
          length: 42
        }, (_, i) => {
          const day = i - firstDay + 1;
          const isCurrentMonth = day > 0 && day <= days;
          const items = isCurrentMonth ? getItemsByDate(day) : [];
          const displayItems = items.slice(0, 3);
          const remaining = items.length - 3;
          return <div key={i} className={`min-h-[120px] p-2 relative ${isCurrentMonth ? dk.surface : dk.surfaceMuted}`} data-id="element-10846" data-mp="18spbdv7lgo5o">
                  {isCurrentMonth && <>
                      <div className={`text-right text-sm mb-1 ${dk.textMuted}`} data-id="element-10847" data-mp="1jcj29t5xlqye">
                        {day}
                      </div>
                      <div className="space-y-1" data-id="element-10848" data-mp="19m6hbzu7fvrq">
                        {displayItems.map(item => <div key={item.id} onClick={() => {
                  if (window.innerWidth < 768) {
                    setMobileSelectedItem(item);
                  } else {
                    navigate(getItemDetailUrl(item));
                  }
                }} onMouseMove={e => {
                  if (window.innerWidth >= 768) {
                    handleMouseMove(e, item);
                  }
                }} onMouseLeave={() => {
                  setHoveredItem(null);
                  setHoverPosition(null);
                }} className="cursor-pointer" data-id="element-10849" data-mp="1ecmztycvu7ky">
                            <div className="px-2 py-1 rounded text-[10px] font-medium text-white truncate shadow-sm hover:opacity-80 active:scale-[0.98] transition-all" style={{
                    backgroundColor: getItemColor(item)
                  }} data-id="element-10850" data-mp="3gayrp5c0so3f">
                              {item.time && <span className="opacity-75 mr-1" data-id="element-10851" data-mp="1vvn1dmmh896x">
                                  {item.time.split(' ')[0]}
                                </span>}
                              {item.title}
                            </div>
                          </div>)}
                        {remaining > 0 && <div className={`text-[10px] font-medium px-2 py-0.5 ${dk.textMuted}`} data-id="element-10852" data-mp="fdmprb4f9hm7">
                            +{remaining} more
                          </div>}
                      </div>
                    </>}
                </div>;
        })}
        </div>
      </div>;
  };
  const renderList = () => <div className="space-y-4" data-id="element-10853" data-mp="z2nccsnttfae">
      {filteredItems.map(item => <Link key={item.id} to={getItemDetailUrl(item)} className={`flex gap-4 p-4 rounded-xl shadow-sm transition-shadow hover:shadow-md block ${dk.card}`} data-id="element-10854" data-mp="1340hf0j3heg9">
          <img src={item.image} alt={item.title} className="w-24 h-36 object-cover rounded-lg shadow-sm flex-shrink-0" data-id="element-10855" data-mp="34uc2avm2jpig" />
          <div className="flex-1 min-w-0 py-1" data-id="element-10856" data-mp="25jc24dtl98v2">
            <div className="flex justify-between items-start" data-id="element-10857" data-mp="25ik6x43wld5b">
              <div data-id="element-10858" data-mp="2jjmujlajqvic">
                <div className="flex items-center gap-2 mb-1" data-id="element-10859" data-mp="2ji1ytschx6iw">
                  <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${item.type === 'event' ? dk.isDark ? 'bg-indigo-900 text-indigo-200' : 'bg-indigo-100 text-indigo-800' : item.type === 'merch' ? dk.badgeNeutral : dk.isDark ? 'bg-indigo-900 text-indigo-200' : 'bg-indigo-100 text-indigo-800'}`} data-id="element-10860" data-mp="2ek0el43c315u">
                    {item.type === 'book' ? 'Book Release' : item.type === 'merch' ? 'Merch Drop' : item.eventType}
                  </span>
                  {item.status && <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${dk.isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-50 text-green-600'}`} data-id="element-10861" data-mp="135mxqj6zzlzb">
                      {item.status.replace('_', ' ')}
                    </span>}
                </div>
                <h3 className={`text-lg font-bold truncate ${dk.textPrimary}`} data-id="element-10862" data-mp="35t3df4r312vy">
                  {item.title}
                </h3>
                <p className={`text-sm ${dk.textSecondary}`} data-id="element-10863" data-mp="39qrb2fk5t9rj">{item.subtitle}</p>
              </div>
              <div className="flex gap-2" onClick={e => e.preventDefault()} data-id="element-10864" data-mp="1bhe1lv4muqm">
                {item.type === 'event' ? <SaveEventButton eventId={Number(item.id)} isSaved={true} onToggleSave={() => {}} data-id="element-10865" data-mp="3oi9wisvsfwia" /> : <SaveToTracker productId={String(item.id)} data-id="element-10866" data-mp="2d2blwkkyp2ey" />}
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-4 text-sm" data-id="element-10867" data-mp="2i2za6yht355h">
              <div className={`flex items-center ${dk.textSecondary}`} data-id="element-10868" data-mp="nlpqkalp8prv">
                <CalendarIcon className="h-4 w-4 mr-2 opacity-70" data-id="element-10869" data-mp="nntugsjllos5" />
                {new Date(item.date).toLocaleDateString(undefined, {
              weekday: 'short',
              month: 'long',
              day: 'numeric'
            })}
              </div>
              {item.time && <div className={`flex items-center ${dk.textSecondary}`} data-id="element-10870" data-mp="9si04m9cu0br">
                  <ClockIcon className="h-4 w-4 mr-2 opacity-70" data-id="element-10871" data-mp="4wsuq2anlp7j" />
                  {item.time}
                </div>}
              {item.location && <div className={`flex items-center ${dk.textSecondary}`} data-id="element-10872" data-mp="3r1o9k31r2dew">
                  <MapPinIcon className="h-4 w-4 mr-2 opacity-70" data-id="element-10873" data-mp="3n14bmx9cr5rp" />
                  {item.location}
                </div>}
            </div>

            <div className="mt-4 flex items-center justify-between" data-id="element-10874" data-mp="1paa22c8hkm2l">
              <div className="flex gap-2 items-center" data-id="element-10875" data-mp="1laj2pk4w8xmg">
                {item.genre && <span className="px-2 py-1 text-xs font-medium rounded text-white" style={{
              backgroundColor: getBookSpineColor(item.genre)
            }} data-id="element-10876" data-mp="337w6k3i75igo">
                    {item.genre}
                  </span>}
                {item.price && <span className={`text-sm font-bold ${dk.textHeading}`} data-id="element-10877" data-mp="3h0c4wr05ksb9">
                    ${item.price}
                  </span>}
              </div>
              <span className={`text-sm font-medium ${rt.accentLink}`} data-id="element-10878" data-mp="2p7v6gq9raf29">
                View Details →
              </span>
            </div>
          </div>
        </Link>)}
    </div>;
  // Grid view styled like UnifiedBookCard
  const renderGrid = () => <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4" data-id="element-10879" data-mp="8j7mkhvxsj5q">
      {filteredItems.map(item => <div key={item.id} className="relative group w-full" data-id="element-10880" data-mp="dfxzwxgcx0ng">
          <Link to={getItemDetailUrl(item)} className="block relative aspect-[2/3] rounded-lg overflow-hidden shadow-md transition-transform duration-200 group-hover:scale-105" data-id="element-10881" data-mp="3l9tg9vtdun1s">
            <img src={item.image} alt={item.title} className="w-full h-full object-cover" loading="lazy" data-id="element-10882" data-mp="3u3q4rha0sbkd" />

            {/* Genre/Type tag top-left */}
            {item.type === 'book' && item.genre && <span className={`absolute top-2 left-2 text-white text-xs ${getGenreColor(item.genre)} px-2 py-0.5 rounded-full`} data-id="element-10883" data-mp="2x58nj53ydgh">
                {item.genre}
              </span>}
            {item.type === 'event' && <span className="absolute top-2 left-2 text-white text-xs bg-indigo-600 px-2 py-0.5 rounded-full" data-id="element-10884" data-mp="csyszop2jb8t">
                {item.eventType}
              </span>}
            {item.type === 'merch' && <span className="absolute top-2 left-2 text-white text-xs bg-gray-600 px-2 py-0.5 rounded-full" data-id="element-10885" data-mp="24ihil0215h9s">
                Merch
              </span>}

            {/* Status badge top-right */}
            {item.status && <span className={`absolute top-2 right-2 text-white text-xs ${getStatusColor(item.status)} px-2 py-0.5 rounded-full`} data-id="element-10886" data-mp="1urwq7ucy98zb">
                {item.status.replace('_', ' ')}
              </span>}

            {/* Hover Overlay - matching UnifiedBookCard style */}
            <div className="absolute inset-0 bg-black/85 backdrop-blur-sm opacity-0 group-hover:opacity-100 flex flex-col p-3 transition-opacity duration-200 text-white z-20" data-id="element-10887" data-mp="1pumphik41aw6">
              {/* Top section */}
              <div className="min-w-0" data-id="element-10888" data-mp="1guhwwejksh22">
                <h3 className="font-bold text-white text-sm line-clamp-1 mb-0.5" data-id="element-10889" data-mp="367blmtpifssb">
                  {item.title}
                </h3>
                {item.series && <span className="inline-block text-[10px] bg-indigo-500/80 text-white px-1.5 py-0.5 rounded mb-1" data-id="element-10890" data-mp="gsy6t70x4c8x">
                    {item.series.name} #{item.series.position}
                  </span>}
                <p className="text-white/80 text-xs line-clamp-1 mb-1.5" data-id="element-10891" data-mp="qqkmhvq1k7ty">
                  by {item.subtitle}
                </p>

                {/* Date/Time info */}
                <div className="text-[11px] text-gray-300 flex items-center mb-1" data-id="element-10892" data-mp="vjngg4yqdjao">
                  <CalendarIcon className="h-3 w-3 mr-1 flex-shrink-0 text-indigo-400" data-id="element-10893" data-mp="zlikhh81qas5" />
                  {new Date(item.date).toLocaleDateString(undefined, {
                month: 'short',
                day: 'numeric'
              })}
                  {item.time && ` • ${item.time}`}
                </div>

                {/* Location for events */}
                {item.location && <div className="text-[11px] text-gray-300 flex items-center mb-1.5" data-id="element-10894" data-mp="3bbbds1hnpdu0">
                    <MapPinIcon className="h-3 w-3 mr-1 flex-shrink-0 text-indigo-400" data-id="element-10895" data-mp="3ga5w72bpp0dl" />
                    <span className="truncate" data-id="element-10896" data-mp="1xe8dyp8l59c0">{item.location}</span>
                  </div>}
              </div>

              {/* Bottom section */}
              <div className="mt-auto flex items-center justify-between" data-id="element-10897" data-mp="1ojtz2nryw0tf">
                {/* Price */}
                {item.price && <span className="text-[11px] font-bold" data-id="element-10898" data-mp="1jjfrt7y6wuwg">${item.price}</span>}

                {/* Save button area */}
                <div className="flex justify-end" onClick={e => e.preventDefault()} data-id="element-10899" data-mp="2caherv84juqj">
                  {item.type === 'event' ? <SaveEventButton eventId={Number(item.id)} isSaved={true} onToggleSave={() => {}} data-id="element-10900" data-mp="22c0hkgilejw1" /> : <SaveToTracker productId={String(item.id)} data-id="element-10901" data-mp="qwbvdrd5xowa" />}
                </div>
              </div>
            </div>
          </Link>

          {/* Title and info below card - matching UnifiedBookCard */}
          <div className="mt-2 text-center" data-id="element-10902" data-mp="xbtwlakb8tqm">
            <h3 className={`font-medium text-sm truncate ${dk.textPrimary}`} data-id="element-10903" data-mp="xar3cvzimf7j">
              {item.title}
            </h3>
            <p className={`text-xs truncate ${dk.textMuted}`} data-id="element-10904" data-mp="obxczncs2l2f">
              {item.subtitle}
            </p>
            {/* Release date */}
            <p className={`text-xs mt-1 ${rt.accentLink}`} data-id="element-10905" data-mp="9lzm2zngrh9j">
              {new Date(item.date).toLocaleDateString(undefined, {
            month: 'short',
            day: 'numeric'
          })}
            </p>
          </div>
        </div>)}
    </div>;
  // Mobile Sections Renderer
  const renderMobileSections = () => <div className="md:hidden space-y-8 mb-8" data-id="element-10906" data-mp="3mu2q6nvftgpp">
      {/* My Saved Section */}
      <section data-id="element-10907" data-mp="3mw5k3izpntzp">
        <div className="flex items-center justify-between mb-3 px-1" data-id="element-10908" data-mp="3rt5qjv0h2yrc">
          <div className="flex items-center gap-2" data-id="element-10909" data-mp="3rse00ylqfr7x">
            <BookmarkIcon className="h-5 w-5 text-indigo-600" data-id="element-10910" data-mp="386kfn8v71rvd" />
            <h2 className="font-bold text-lg" data-id="element-10911" data-mp="3896ultkez9nm">My Saved</h2>
          </div>
          <button onClick={() => setViewMode('list')} className="text-sm text-indigo-600 font-medium" data-id="element-10912" data-mp="33a3rienzvdia">
            See All
          </button>
        </div>
        {/* Horizontal Scroll */}
        <div className="flex overflow-x-auto gap-3 pb-4 -mx-4 px-4 scrollbar-hide snap-x snap-mandatory" data-id="element-10913" data-mp="3d8t7u308spwf">
          {mockItems.filter(i => i.isSaved).map(item => <div key={item.id} className="snap-start flex-shrink-0 w-28 cursor-pointer" onClick={() => setMobileSelectedItem(item)} data-id="element-10914" data-mp="3m5akxbs62xut">
                <div className="relative rounded-lg overflow-hidden shadow-sm aspect-[2/3] mb-2" data-id="element-10915" data-mp="3vzarli33kb5n">
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover" data-id="element-10916" data-mp="doz51rfvms1m" />
                  {/* Date Badge */}
                  <div className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-[1px] p-1 text-center" data-id="element-10917" data-mp="287a6r3rf2u6g">
                    <span className="text-[10px] font-bold text-white" data-id="element-10918" data-mp="2cy0bqfvxkv0m">
                      {new Date(item.date).toLocaleDateString(undefined, {
                  month: 'short',
                  day: 'numeric'
                })}
                    </span>
                  </div>
                </div>
                <h3 className={`text-xs font-bold leading-tight line-clamp-2 mb-0.5 ${dk.textPrimary}`} data-id="element-10919" data-mp="1p9h4m3omaoa4">
                  {item.title}
                </h3>
                <p className={`text-[10px] truncate ${dk.textMuted}`} data-id="element-10920" data-mp="1yafcwy639hyi">
                  {item.subtitle}
                </p>
              </div>)}
        </div>
      </section>

      {/* New Releases Section */}
      <section data-id="element-10921" data-mp="39mpu07bgzpn4">
        <div className="flex items-center justify-between mb-3 px-1" data-id="element-10922" data-mp="39qlzmw57rdbz">
          <div className="flex items-center gap-2" data-id="element-10923" data-mp="y0nzo1p1bduz">
            <SparklesIcon className="h-5 w-5 text-indigo-600" data-id="element-10924" data-mp="tzudbcr8q747" />
            <h2 className="font-bold text-lg" data-id="element-10925" data-mp="u0drcidrsbcy">New Releases</h2>
          </div>
          <Link to="/browse" className="text-sm text-indigo-600 font-medium" data-id="element-10926" data-mp="p7kmzvm7r51e">
            Browse All
          </Link>
        </div>
        {/* Horizontal Scroll */}
        <div className="flex overflow-x-auto gap-3 pb-4 -mx-4 px-4 scrollbar-hide snap-x snap-mandatory" data-id="element-10927" data-mp="fb7n2g02ruy7">
          {discoveryItems.map(item => <div key={item.id} className={`snap-start flex-shrink-0 w-64 p-3 rounded-xl shadow-sm border ${dk.card}`} data-id="element-10928" data-mp="aaa5o5mw34xr">
              <div className="flex gap-3" data-id="element-10929" data-mp="1bon4ahf3pwz">
                <Link to={getItemDetailUrl(item)} className="flex-shrink-0 w-20 h-28 rounded-lg overflow-hidden shadow-sm" data-id="element-10930" data-mp="1czusldzhhtd">
                  <img src={item.image} alt={item.title} className="w-full h-full object-cover" data-id="element-10931" data-mp="1vphb190enhoi" />
                </Link>
                <div className="flex-1 min-w-0 flex flex-col" data-id="element-10932" data-mp="2jlud5psvgegs">
                  <Link to={getItemDetailUrl(item)} data-id="element-10933" data-mp="25tefz5mnjans">
                    <h3 className={`font-bold text-sm leading-tight line-clamp-2 mb-1 ${dk.textHeading}`} data-id="element-10934" data-mp="25qrzgghs9ktf">
                      {item.title}
                    </h3>
                  </Link>
                  <p className={`text-xs mb-2 ${dk.textMuted}`} data-id="element-10935" data-mp="32ivjpf22qmmu">
                    {item.subtitle}
                  </p>
                  <div className="mt-auto flex items-center justify-between" data-id="element-10936" data-mp="2oib0wiw5sqtc">
                    <span className={`text-xs font-medium ${rt.accentLink}`} data-id="element-10937" data-mp="2thxqlrt6bzj1">
                      {new Date(item.date).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric'
                  })}
                    </span>
                    <SaveToTracker productId={String(item.id)} data-id="element-10938" data-mp="1ajrclgc0331i" />
                  </div>
                </div>
              </div>
            </div>)}
        </div>
      </section>
    </div>;
  return <div className={`min-h-screen py-8 ${dk.page}`} data-id="element-10939" data-mp="1jk7eild3i249">
      {/* Hover Card (Desktop) */}
      {hoveredItem && hoverPosition && <HoverCard item={hoveredItem} data-id="element-10940" data-mp="1emfo5vs3mo26" />}

      {/* Mobile Item Popup */}
      {mobileSelectedItem && <MobileItemPopup item={mobileSelectedItem} onClose={() => setMobileSelectedItem(null)} data-id="element-10941" data-mp="qwtpd9fn2aup" />}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-10942" data-mp="ms3thkkzcw9s">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4" data-id="element-10943" data-mp="10n6b34z6wa2f">
          <div data-id="element-10944" data-mp="vtk5fwrg16g4">
            <h1 className="text-3xl font-bold mb-2" data-id="element-10945" data-mp="vt1xw3zbz5sw">My Tracker</h1>
            <p className={`max-w-2xl ${dk.textSecondary}`} data-id="element-10946" data-mp="83ps7e37pfvc">
              Keep track of your upcoming book releases, merchandise drops, and
              events all in one place.
            </p>
          </div>

          <div className={`flex items-center gap-1 p-1.5 rounded-lg shadow-sm ${dk.card}`} data-id="element-10947" data-mp="3erl265awl7uo">
            <button onClick={() => setViewMode('list')} className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? dk.isDark ? 'bg-indigo-900/50 text-indigo-400' : 'bg-indigo-100 text-indigo-600' : `${dk.textMuted} hover:${dk.textPrimary} hover:${dk.surfaceMuted}`}`} title="List View" data-id="element-10948" data-mp="3it6o0g5f0wh0">
              <ListIcon className="h-5 w-5" data-id="element-10949" data-mp="5js9i99hqr0r" />
            </button>
            <button onClick={() => setViewMode('grid')} className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? dk.isDark ? 'bg-indigo-900/50 text-indigo-400' : 'bg-indigo-100 text-indigo-600' : `${dk.textMuted} hover:${dk.textPrimary} hover:${dk.surfaceMuted}`}`} title="Grid View" data-id="element-10950" data-mp="abts0cx95dz8">
              <GridIcon className="h-5 w-5" data-id="element-10951" data-mp="p92m3edxwvun" />
            </button>
            <button onClick={() => setViewMode('calendar')} className={`p-2 rounded-md transition-colors ${viewMode === 'calendar' ? dk.isDark ? 'bg-indigo-900/50 text-indigo-400' : 'bg-indigo-100 text-indigo-600' : `${dk.textMuted} hover:${dk.textPrimary} hover:${dk.surfaceMuted}`}`} title="Calendar View" data-id="element-10952" data-mp="29r4zs05n6iv6">
              <CalendarIcon className="h-5 w-5" data-id="element-10953" data-mp="2shcz07680p63" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className={`border-b mb-8 ${dk.border}`} data-id="element-10954" data-mp="2nhqedco2c6n9">
          <nav className="-mb-px flex space-x-8 overflow-x-auto scrollbar-hide" data-id="element-10955" data-mp="2nkctbxda9ofi">
            <button onClick={() => setActiveTab('all')} className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${activeTab === 'all' ? dk.isDark ? 'border-indigo-400 text-indigo-400' : 'border-indigo-500 text-indigo-600' : `border-transparent ${dk.textMuted} hover:${dk.textPrimary} hover:border-gray-600`}`} data-id="element-10956" data-mp="2ipyvlob5f57o">
              All Activity
            </button>
            <button onClick={() => setActiveTab('saved')} className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap flex items-center gap-1.5 ${activeTab === 'saved' ? dk.isDark ? 'border-indigo-400 text-indigo-400' : 'border-indigo-500 text-indigo-600' : `border-transparent ${dk.textMuted} hover:${dk.textPrimary} hover:border-gray-600`}`} data-id="element-10957" data-mp="10qolunv20vof">
              <BookmarkIcon className="h-4 w-4" data-id="element-10958" data-mp="hznqe9t57i9m" />
              My Saved
              {mockItems.filter(i => i.isSaved).length > 0 && <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeTab === 'saved' ? dk.isDark ? 'bg-indigo-900/50 text-indigo-300' : 'bg-indigo-100 text-indigo-600' : dk.badgeNeutral}`} data-id="element-10959" data-mp="d1m5rkg2jaw1">
                  {mockItems.filter(i => i.isSaved).length}
                </span>}
            </button>
            <button onClick={() => setActiveTab('releases')} className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${activeTab === 'releases' ? dk.isDark ? 'border-indigo-400 text-indigo-400' : 'border-indigo-500 text-indigo-600' : `border-transparent ${dk.textMuted} hover:${dk.textPrimary} hover:border-gray-600`}`} data-id="element-10960" data-mp="2axklnl9v8x9a">
              Releases & Drops
            </button>
            <button onClick={() => setActiveTab('events')} className={`pb-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap ${activeTab === 'events' ? dk.isDark ? 'border-indigo-400 text-indigo-400' : 'border-indigo-500 text-indigo-600' : `border-transparent ${dk.textMuted} hover:${dk.textPrimary} hover:border-gray-600`}`} data-id="element-10961" data-mp="1rzzvmavd7sn3">
              Events
            </button>
          </nav>
        </div>

        {/* Legend (Calendar View Only) */}
        {viewMode === 'calendar' && <div className="flex flex-wrap gap-4 mb-6 text-xs" data-id="element-10962" data-mp="2ropn02mxxg0x">
            <div className="flex items-center" data-id="element-10963" data-mp="2rr2bkyn7eomy">
              <span className="w-3 h-3 rounded-full bg-[#E11D48] mr-2" data-id="element-10964" data-mp="2mqo1lb21l4ma"></span>
              <span className={dk.textSecondary} data-id="element-10965" data-mp="31mvl095mm20i">Romance</span>
            </div>
            <div className="flex items-center" data-id="element-10966" data-mp="31ksxpwvuymzh">
              <span className="w-3 h-3 rounded-full bg-[#7E22CE] mr-2" data-id="element-10967" data-mp="2wp3t3f4zefwb"></span>
              <span className={dk.textSecondary} data-id="element-10968" data-mp="28xydn12ydpmj">Fantasy</span>
            </div>
            <div className="flex items-center" data-id="element-10969" data-mp="23zes31s9ufup">
              <span className="w-3 h-3 rounded-full bg-[#2563EB] mr-2" data-id="element-10970" data-mp="240pt4tuc19i4"></span>
              <span className={dk.textSecondary} data-id="element-10971" data-mp="2ioj94fnbye18">Sci-Fi</span>
            </div>
            <div className="flex items-center" data-id="element-10972" data-mp="2dxujz6z4ht59">
              <span className="w-3 h-3 rounded-full bg-[#CA8A04] mr-2" data-id="element-10973" data-mp="2dvzxho87dsz2"></span>
              <span className={dk.textSecondary} data-id="element-10974" data-mp="1q6yitj8s1llc">Mystery</span>
            </div>
            <div className="flex items-center" data-id="element-10975" data-mp="1l9f5cljwlcug">
              <span className="w-3 h-3 rounded-full bg-[#4F46E5] mr-2" data-id="element-10976" data-mp="1l8na5bu7xh4p"></span>
              <span className={dk.textSecondary} data-id="element-10977" data-mp="q2z1q44b9uo">Events</span>
            </div>
            <div className="flex items-center" data-id="element-10978" data-mp="5husq9iklvp1">
              <span className="w-3 h-3 rounded-full bg-[#6B7280] mr-2" data-id="element-10979" data-mp="5jo0js4x6lym"></span>
              <span className={dk.textSecondary} data-id="element-10980" data-mp="3mznrfjznkham">Merch</span>
            </div>
          </div>}

        {/* Content */}
        {viewMode === 'calendar' && renderCalendar()}
        {viewMode === 'list' && renderList()}
        {viewMode === 'grid' && renderGrid()}

        {/* Empty State */}
        {filteredItems.length === 0 && <div className="text-center py-12" data-id="element-10981" data-mp="ago6m31rropq">
            <div className={`inline-flex p-4 rounded-full mb-4 ${dk.surface}`} data-id="element-10982" data-mp="afwc6vjwrx11">
              {activeTab === 'saved' ? <BookmarkIcon className="h-8 w-8 text-gray-400" data-id="element-10983" data-mp="eggai2g7x6or" /> : <BellIcon className="h-8 w-8 text-gray-400" data-id="element-10984" data-mp="2v5bz3fg0b1le" />}
            </div>
            <h3 className="text-lg font-medium mb-2" data-id="element-10985" data-mp="2za1t0yr3u63o">
              {activeTab === 'saved' ? 'No saved items yet' : 'Nothing tracked yet'}
            </h3>
            <p className={`mb-6 ${dk.textMuted}`} data-id="element-10986" data-mp="3ao4t7lyhf4r7">
              {activeTab === 'saved' ? 'Save books, merch, and events by clicking the bookmark icon to see them here.' : 'Start tracking books, merch, and events to see them here.'}
            </p>
            <div className="flex justify-center gap-4" data-id="element-10987" data-mp="29czu9yfy688">
              <Link to="/browse" className={`px-4 py-2 text-white rounded-md transition-colors ${rt.primaryButton}`} data-id="element-10988" data-mp="26qtuer77ys4">
                Browse Books
              </Link>
              <Link to="/events" className={`px-4 py-2 rounded-md transition-colors ${dk.badgeNeutral} hover:opacity-80`} data-id="element-10989" data-mp="3keq0dxs81hlq">
                Find Events
              </Link>
            </div>
          </div>}
      </div>
    </div>;
};
export default MyTracker;